import React from "react";
import { List, ListItem, ListItemText, Paper, IconButton, Box, Typography, Avatar } from "@mui/material";
import { 
  DragHandle as DragHandleIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const StepsList = ({ steps = [], onStepSelect, onStepReorder, onStepDelete }) => {
  // Debug the steps data
  console.log('StepsList received steps:', steps);
  console.log('Steps type:', typeof steps);
  console.log('Is steps array:', Array.isArray(steps));
  
  // Ensure steps is always an array
  const safeSteps = Array.isArray(steps) ? steps : [];
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    if (result.destination.index === result.source.index) return;
    
    onStepReorder(result.source.index, result.destination.index);
  };

  const getStepIcon = (action) => {
    switch(action) {
      case 'click':
        return '👆';
      case 'type':
      case 'input':
        return '✏️';
      case 'select':
        return '🔍';
      case 'navigate':
        return '🧭';
      case 'wait':
        return '⏱️';
      default:
        return '⚡';
    }
  };

  const getStepColor = (action) => {
    switch(action) {
      case 'click':
        return '#2196f3'; // Blue
      case 'type':
      case 'input':
        return '#4caf50'; // Green
      case 'select':
        return '#ff9800'; // Orange
      case 'navigate':
        return '#9c27b0'; // Purple
      case 'wait':
        return '#607d8b'; // Blue Grey
      default:
        return '#757575'; // Grey
    }
  };

  return (
    <Paper elevation={2} sx={{ mb: 3, maxHeight: "600px", overflow: "auto" }}>
      <Typography variant="h6" sx={{ p: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}>
        Steps
      </Typography>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="steps-list">
          {(provided) => (
            <List {...provided.droppableProps} ref={provided.innerRef}>
              {/* Debug info */}
              {safeSteps.length === 0 && (
                <ListItem sx={{ padding: 2, justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No steps found. Steps data: {JSON.stringify(steps).substring(0, 100)}
                  </Typography>
                </ListItem>
              )}
              {safeSteps.map((step, index) => (
                <Draggable key={step.id} draggableId={`step-${step.id}`} index={index}>
                  {(provided) => (
                    <ListItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      <Box {...provided.dragHandleProps} sx={{ mr: 1 }}>
                        <DragHandleIcon color="action" />
                      </Box>
                      <Avatar 
                        sx={{ 
                          mr: 2, 
                          bgcolor: getStepColor(step.action || step.type),
                          width: 32,
                          height: 32,
                          fontSize: '18px'
                        }}
                      >
                        {getStepIcon(step.action || step.type)}
                      </Avatar>
                      <ListItemText
                        primary={`Step ${index + 1}: ${step.name || step.action || step.type || 'Unknown'}`}
                        secondary={
                          step.inputData && step.inputData.selector
                            ? `Selector: ${step.inputData.selector}${step.inputData.value ? `, Value: ${step.inputData.value}` : ""}`
                            : step.selector
                            ? `Selector: ${step.selector}${step.value ? `, Value: ${step.value}` : ""}`
                            : step.inputData && step.inputData.value
                            ? `Value: ${step.inputData.value}`
                            : step.value
                            ? `Value: ${step.value}`
                            : step.notes || ""
                        }
                        onClick={() => onStepSelect(step)}
                        sx={{ cursor: "pointer" }}
                      />
                      <IconButton size="small" color="primary" onClick={() => onStepSelect(step)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => onStepDelete(step.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    </Paper>
  );
};

export default StepsList;
